/**
 * Announcement bar slider.
 *
 * Uses the GLOBAL Swiper (assets/10X_swiper.min.js, loaded blocking in
 * layout/theme.liquid <head>) — never bundled here. This file only mounts it.
 *
 * Two bugs from the old inline init are fixed:
 *  1. nav was bound with the GLOBAL selectors '.swiper-button-next/prev', which
 *     sections/testimonials.liquid also binds — the two sliders hijacked each
 *     other on any page holding both. Nav is now resolved to NODES inside the
 *     bar's own root, and renamed to .announcement-bar__nav--*.
 *  2. Swiper was constructed even for a single slide (dead arrows, wasted work).
 *
 * Markup contract — sections/announcement-bar.liquid:
 *   [data-announcement-bar]                        root
 *     data-autoplay="true|false"
 *     data-autoplay-interval="<ms>"
 *     .announce_slider                             the Swiper element
 *       .swiper-wrapper > .swiper-slide            slides
 *     .announcement-bar__nav--prev / --next        <button>, only when >1 slide
 */

const REDUCED_MOTION = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Keyed by section id so theme-editor re-renders can destroy the old instance
// instead of leaving an orphaned autoplay timer running against dead DOM.
const instances = new Map();

const sectionIdOf = (root) => {
    const section = root.closest('.shopify-section');
    return (section && section.id) || 'announcement-bar';
};

// Marquee is opt-in (root gets .announcement-bar--marquee from the section
// setting) AND only kicks in per-message when that message can't fit on one
// line — otherwise the static centred layout is kept. CSS can't measure text
// width, so the decision is made here.
const applyMarquee = (root) => {
    if (!root.classList.contains('announcement-bar--marquee')) return;

    const reduce = REDUCED_MOTION();

    root.querySelectorAll('.announcement-bar__message').forEach((message) => {
        // Measure in the STATIC state (nowrap + overflow hidden) so scrollWidth is
        // the full copy width and clientWidth is what actually fits. Removing the
        // class first forces that reflow before we read.
        message.classList.remove('is-marquee');

        const group = message.querySelector(
            '.announcement-bar__group:not([aria-hidden="true"])',
        );
        if (!group) return;

        // Only the breakpoint-visible text span(s) count (offsetParent is null for
        // the display:none one). >1px guards sub-pixel rounding.
        const overflows = Array.from(
            group.querySelectorAll('.announcement-bar__text'),
        )
            .filter((el) => el.offsetParent !== null)
            .some((el) => el.scrollWidth - el.clientWidth > 1);

        message.classList.toggle('is-marquee', overflows && !reduce);
    });
};

// One shared, debounced resize pass — breakpoints and the mobile/desktop span
// swap both change whether copy fits.
let resizeRaf = 0;
const onResize = () => {
    if (resizeRaf) return;
    resizeRaf = window.requestAnimationFrame(() => {
        resizeRaf = 0;
        document
            .querySelectorAll('[data-announcement-bar]')
            .forEach(applyMarquee);
    });
};

const destroyBar = (key) => {
    const swiper = instances.get(key);
    if (!swiper) return;

    try {
        swiper.destroy(true, true);
    } catch (error) {
        // A destroy on already-detached DOM must never break the rest of init.
        console.warn('[announcement-bar] destroy failed', error);
    }

    instances.delete(key);
};

const setupBar = (root) => {
    if (typeof window.Swiper !== 'function') {
        console.warn('[announcement-bar] global Swiper missing — bar stays static');
        return;
    }

    const slider = root.querySelector('.announce_slider');
    if (!slider) return;

    const slides = slider.querySelectorAll('.swiper-slide');
    if (slides.length < 2) return; // one message: nothing to slide

    const prev = root.querySelector('.announcement-bar__nav--prev');
    const next = root.querySelector('.announcement-bar__nav--next');

    // Autoplay needs a WCAG 2.2.2 pause mechanism; pauseOnMouseEnter covers the
    // pointer, focusin/focusout covers keyboard users.
    const interval = parseInt(root.dataset.autoplayInterval, 10);
    const wantsAutoplay = root.dataset.autoplay === 'true' && !REDUCED_MOTION();

    const options = {
        loop: true,
        a11y: true, // Swiper owns the live region — never also add aria-live
        autoHeight: false,
    };

    if (prev && next) {
        // Node references, not selector strings — see the header comment.
        options.navigation = { nextEl: next, prevEl: prev };
    }

    if (wantsAutoplay) {
        options.autoplay = {
            delay: interval > 0 ? interval : 5000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
        };
    }

    let swiper;

    try {
        swiper = new window.Swiper(slider, options);
    } catch (error) {
        console.error('[announcement-bar] Swiper init failed', error);
        return;
    }

    const key = sectionIdOf(root);
    destroyBar(key);
    instances.set(key, swiper);

    if (wantsAutoplay && swiper.autoplay) {
        root.addEventListener('focusin', () => swiper.autoplay.stop());
        root.addEventListener('focusout', () => swiper.autoplay.start());
    }
};

const initBars = (scope) => {
    const context = scope && scope.querySelectorAll ? scope : document;

    context.querySelectorAll('[data-announcement-bar]').forEach((root) => {
        if (root.dataset.announcementInit === 'true') return;
        root.dataset.announcementInit = 'true';
        setupBar(root);

        // Measure now, again once the webfont (Inter) settles since text width
        // shifts when it swaps in, and on every resize.
        applyMarquee(root);
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => applyMarquee(root));
        }
    });

    window.removeEventListener('resize', onResize);
    window.addEventListener('resize', onResize);
};

export default function initAnnouncementBar() {
    initBars(document);
}

/** Theme editor re-renders the header group without re-firing DOMContentLoaded. */
export function reloadAnnouncementBar(target) {
    if (!target || !target.querySelector) return;
    if (!target.querySelector('[data-announcement-bar]')) return;

    destroyBar(target.id || 'announcement-bar');
    initBars(target);
}

/** Section removed in the editor — kill its autoplay timer. */
export function unloadAnnouncementBar(target) {
    if (!target) return;
    destroyBar(target.id || 'announcement-bar');
}
